import { useCallback, useContext, useMemo, useState, useRef, useEffect } from 'react';
import { produce } from 'immer';
import { getUserSettings, updateUserSettings } from 'src/utils/apiRequests';
import { useResponse } from './api/useResponse';
import { SettingsContext } from 'src/contexts/SettingsContext';
import useAuth from './useAuth';

/* ------------------------------------------------------------------ */
/*  Consumer helper – exposes setMultipleSettings with delete support  */
/* ------------------------------------------------------------------ */
export const useSettingsContext = () => {
  const {
    settings,
    setSetting,
    setMultipleSettings,
    deleteSetting,
    settingsReady,
  } = useContext(SettingsContext);

  return { settings, setSetting, setMultipleSettings, deleteSetting, settingsReady };
};

/* ------------------------------------------------------------------ */
/*  Main hook that talks to the API                                    */
/* ------------------------------------------------------------------ */

type SettingPath = string | string[];

export interface SettingPatch {
  key: SettingPath;
  /** write this value (ignored when `delete` === true) */
  value?: any;
  /** if true the key path is removed instead of written */
  delete?: boolean;
}

type RawSettingsPayload = { settings: string } | null;
export type ParsedSettings = Record<string, any>;

const STORAGE_KEY = 'app.userSettings';

const isPlainObject = (v: any) => !!v && typeof v === 'object' && !Array.isArray(v);

export function useUserSettings(): [
  ParsedSettings,
  /* setSetting          */ (key: SettingPath, value: any) => void,
  /* setMultipleSettings */ (patches: SettingPatch | SettingPatch[]) => void,
  /* deleteSetting       */ (key: SettingPath) => void
] {
  const { isAuthenticated } = useAuth();

  /* ---- GET --------------------------------------------------------- */
  const { rows: rawResult, setNewValue } = useResponse<RawSettingsPayload>({
    requestFunction: getUserSettings,
    dependency: isAuthenticated,
  });

  // Always parse into a safe object (never null/undefined)
  const rawSettingsStr = rawResult?.settings ?? '';
  const parsedSettings = useMemo<ParsedSettings>(() => {
    if (!rawSettingsStr) return {};
    try {
      const parsed = JSON.parse(rawSettingsStr);
      return isPlainObject(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }, [rawSettingsStr]);

  /* Track a safe base and hydration state ---------------------------- */
  const lastGoodRef = useRef<ParsedSettings>({});
  const [hasHydratedFromServer, setHasHydratedFromServer] = useState(false);

  // Snapshot initial localStorage once so we can safely decide when to seed "{}"
  const initialLocalRef = useRef<ParsedSettings | null>(null);
  useEffect(() => {
    if (initialLocalRef.current !== null) return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (isPlainObject(parsed)) {
          initialLocalRef.current = parsed;
        }
      }
    } catch {
      /* noop */
    }
  }, []);

  // Seed with localStorage on first mount if nothing else is present
  useEffect(() => {
    if (Object.keys(lastGoodRef.current).length > 0) return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (isPlainObject(parsed)) lastGoodRef.current = parsed;
      }
    } catch {
      /* noop */
    }
  }, []);

  // Only consider ourselves "hydrated" once rows is actually present (non-undefined)
  const hasRows = rawResult !== undefined && rawResult !== null;

  useEffect(() => {
    if (!hasRows) return;
    lastGoodRef.current = parsedSettings;
    if (!hasHydratedFromServer) setHasHydratedFromServer(true);
  }, [hasRows, parsedSettings, hasHydratedFromServer]);

  /* ---- PUT helpers ------------------------------------------------- */
  const [uploadPayload, setUploadPayload] = useState<any>(null);

  useResponse({
    requestFunction: updateUserSettings,
    values: [uploadPayload],
    requestNothingOnNull: true,
    dependency: uploadPayload,
  });

  /* –– debounced uploader ––––––––––––––––––––––––––––––––––––––––––– */
  const flushRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleUpload = useCallback(
    (payload: RawSettingsPayload) => {
      if (flushRef.current) clearTimeout(flushRef.current);
      flushRef.current = setTimeout(() => setUploadPayload(payload), 150);
    },
    []
  );

  /* –– queue patches until we have a base ––––––––––––––––––––––––––– */
  const queuedPatchesRef = useRef<SettingPatch[]>([]);

  /* –– batch writer ––––––––––––––––––––––––––––––––––––––––––––––––– */
  const updateMultipleSettings = useCallback(
    (patchInput: SettingPatch | SettingPatch[]) => {
      const patches = Array.isArray(patchInput) ? patchInput : [patchInput];

      if (!lastGoodRef.current) {
        queuedPatchesRef.current.push(...patches);
        console.warn('Settings not ready; queued patches:', patches);
        return;
      }

      const nextSettingsObject: ParsedSettings = produce(
        lastGoodRef.current,
        (draft) => {
          patches.forEach(({ key, value, delete: isDelete }) => {
            const pathArr = Array.isArray(key) ? key : [key];
            let target: any = draft;

            for (let i = 0; i < pathArr.length - 1; i++) {
              const k = pathArr[i];
              if (!isPlainObject(target[k])) {
                target[k] = {};
              }
              target = target[k];
            }

            const finalKey = pathArr[pathArr.length - 1];

            if (isDelete) {
              if (finalKey in target) {
                delete target[finalKey];
              }
            } else {
              // Optional micro-guard: skip overwrite with identical primitive
              const prevVal = target[finalKey];
              if (
                prevVal === value ||
                (typeof prevVal === 'object' &&
                  prevVal !== null &&
                  typeof value === 'object' &&
                  value !== null &&
                  JSON.stringify(prevVal) === JSON.stringify(value))
              ) {
                return;
              }
              target[finalKey] = value;
            }
          });
        }
      );

      // 🔑 If nothing changed, Immer returns the same reference.
      if (nextSettingsObject === lastGoodRef.current) {
        return;
      }

      try {
        const stringified = JSON.stringify(nextSettingsObject);
        const nextRawPayload: RawSettingsPayload = { settings: stringified };

        lastGoodRef.current = nextSettingsObject;
        setNewValue(nextRawPayload);

        if (hasHydratedFromServer) {
          scheduleUpload(nextRawPayload);
        } else {
          queuedPatchesRef.current.push(...patches);
        }
      } catch (e) {
        console.error('Failed to stringify updated settings:', e, nextSettingsObject);
      }
    },
    [setNewValue, scheduleUpload, hasHydratedFromServer]
  );



  // Flush queued patches once hydrated
  useEffect(() => {
    if (!hasHydratedFromServer) return;
    if (!queuedPatchesRef.current.length) return;
    const pending = queuedPatchesRef.current.splice(0);
    updateMultipleSettings(pending);
  }, [hasHydratedFromServer, updateMultipleSettings]);

  /* –– single-key helper (wraps batch) –––––––––––––––––––––––––––––– */
  const updateSetting = useCallback(
    (key: SettingPath, value: any) => {
      updateMultipleSettings({ key, value });
    },
    [updateMultipleSettings]
  );

  /* –– delete helper (wraps batch with delete flag) ––––––––––––––––– */
  const deleteSetting = useCallback(
    (key: SettingPath) => updateMultipleSettings({ key, delete: true }),
    [updateMultipleSettings]
  );

  /* ------------------------------------------------------------------ */
  /*  Seed server with {"settings":"{}"} only in truly-empty legacy case */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (!hasHydratedFromServer) return;

    // If the server returned nothing / invalid / "", we parsed it to {}.
    const nothingFromServer = !rawSettingsStr;

    // IMPORTANT: only consider seeding "{}" if we actually had some local
    // snapshot at startup. If the user wiped all site data, this stays null
    // and we *never* auto-overwrite the server.
    const hadLocalAtStartup = initialLocalRef.current !== null;
    const startupLocalWasEmpty =
      hadLocalAtStartup &&
      Object.keys(initialLocalRef.current || {}).length === 0;

    if (nothingFromServer && startupLocalWasEmpty) {
      const payload = { settings: '{}' };
      // Optimistically update cache and upload
      setNewValue(payload);
      scheduleUpload(payload);
    }
  }, [hasHydratedFromServer, rawSettingsStr, scheduleUpload, setNewValue]);

  return [parsedSettings, updateSetting, updateMultipleSettings, deleteSetting];
}
